import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/internal/Observable';
import {HttpClient} from '@angular/common/http';
import {UserTagModel} from '../models/user-tag.model';
import {AddArtistsToTagModel} from '../models/add-artists-to-tag.model';
import {Subject} from "rxjs";
import {AddAlbumsToTagModel} from "../models/add-albums-to-tag.model";
import {FoldersAndTagsModel} from "../models/folders-and-tags.model";
import {plainToClass} from "class-transformer";
import {map} from "rxjs/operators";
import {ArtistsByTagModel} from "../models/artists-by-tag.model";
import {AlbumsByTagModel} from "../models/albums-by-tag.model";
import {FolderModel} from "../models/folder.model";
import {FolderTagModel} from "../models/folder-tag.model";
import {AddTagModel} from "../models/add-tag.model";
import {AddFolderModel} from "../models/add-folder.model";
import {EditTagModel} from "../models/edit-tag.model";

@Injectable()
export class SpotifyTagsService {

  private tagToEditSource = new Subject<UserTagModel>();
  tagToEdit$ = this.tagToEditSource.asObservable();

  private tagEditedSource = new Subject<UserTagModel>();
  tagEdited$ = this.tagEditedSource.asObservable();

  private tagToDeleteSource = new Subject<UserTagModel>();
  tagToDelete$ = this.tagToDeleteSource.asObservable();

  private folderToEditSource = new Subject<FolderTagModel>();
  folderToEdit$ = this.folderToEditSource.asObservable();

  private folderEditedSource = new Subject<string>();
  folderEdited$ = this.folderEditedSource.asObservable();

  private folderToDeleteSource = new Subject<number>();
  folderToDelete$ = this.folderToDeleteSource.asObservable();

  private alertSource = new Subject<any>();
  alert$ = this.alertSource.asObservable();

  private navigationSource = new Subject<any>();
  navigation$ = this.navigationSource.asObservable();

  constructor(private http: HttpClient) { }

  passEditTag(tag: UserTagModel) {
    this.tagToEditSource.next(tag);
  }

  passDeleteTag(tag: UserTagModel) {
    this.tagToDeleteSource.next(tag);
  }

  tagEdited(tag: UserTagModel) {
    this.tagEditedSource.next(tag);
  }

  passEditFolder(folder: FolderTagModel) {
    this.folderToEditSource.next(folder);
  }

  folderEdited(folderName: string) {
    this.folderEditedSource.next(folderName);
  }

  passDeleteFolder(folderId: number) {
    this.folderToDeleteSource.next(folderId);
  }

  error() {
    this.alertSource.next();
  }

  navigate() {
    this.navigationSource.next();
  }

  getArtists(tagId: number): Observable<ArtistsByTagModel> {
    if (tagId) {
      return this.http.get<ArtistsByTagModel>("resource/artists" + "?tagId=" + tagId).pipe(
      map(artists => plainToClass(ArtistsByTagModel, artists))
      );
    }
    return this.http.get<ArtistsByTagModel>("resource/artists").pipe(
      map(artists => plainToClass(ArtistsByTagModel, artists))
    );
  }

  getAlbums(tagId: number): Observable<AlbumsByTagModel> {
    if (tagId) {
      return this.http.get<AlbumsByTagModel>("resource/albums" + "?tagId=" + tagId).pipe(
        map(albums => plainToClass(AlbumsByTagModel, albums))
      );
    }
    return this.http.get<AlbumsByTagModel>("resource/albums").pipe(
      map(albums => plainToClass(AlbumsByTagModel, albums))
    );
  }

  getTags(): Observable<FoldersAndTagsModel> {
    return this.http.get<FoldersAndTagsModel>("resource/tags").pipe(
      map(foldersAndTags => plainToClass(FoldersAndTagsModel, foldersAndTags))
    )
  }

  syncWithSpotify(): Observable<any> {
    return this.http.post<any>("resource/synchronise", null);
  }

  addTag(addTag: AddTagModel): Observable<FoldersAndTagsModel> {
    return this.http.post<FoldersAndTagsModel>("resource/tags", addTag).pipe(
      map(foldersAndTags => plainToClass(FoldersAndTagsModel, foldersAndTags))
    );
  }

  editTag(existingTag: UserTagModel, newTag: EditTagModel): Observable<FoldersAndTagsModel> {
    const request = {
      idOfTagToEdit: existingTag.tagId,
      newTag: newTag.tag,
      tagType: existingTag.tagType,
      folderId: newTag.folderId
    };
    return this.http.post<FoldersAndTagsModel>("resource/tags/edit", request).pipe(
      map(foldersAndTags => plainToClass(FoldersAndTagsModel, foldersAndTags))
    );
  }

  deleteTag(tagId: number): Observable<any> {
    return this.http.delete<any>("resource/tags/" + tagId + "/delete");
  }

  getArtistsByTag(tag: UserTagModel): Observable<AddArtistsToTagModel[]> {
    return this.http.get<AddArtistsToTagModel[]>("resource/artists/" + tag.tagId).pipe(
      map(artistsByTag => plainToClass(AddArtistsToTagModel, artistsByTag))
    );
  }

  addArtistsToTag(tag: UserTagModel, selectedArtists: AddArtistsToTagModel[]): Observable<any> {
    return this.http.post<any>("resource/artists/" + tag.tagId, selectedArtists);
  }

  addFolder(addFolder: AddFolderModel): Observable<FoldersAndTagsModel> {
    return this.http.post<FoldersAndTagsModel>("resource/tags/folder/add", addFolder).pipe(
      map(foldersAndTags => plainToClass(FoldersAndTagsModel, foldersAndTags))
    );
  }

  editFolder(existingFolder: FolderModel, newFolder: string): Observable<FoldersAndTagsModel> {
    const request = {
      idOfFolderToEdit: existingFolder.folderId,
      newFolder: newFolder,
      tagType: existingFolder.tagType
    };
    return this.http.post<FoldersAndTagsModel>("resource/tags/folder/edit", request).pipe(
      map(foldersAndTags => plainToClass(FoldersAndTagsModel, foldersAndTags))
    );
  }

  deleteFolder(folderId: number): Observable<any> {
    return this.http.delete<any>("resource/tags/folder/" + folderId + "/delete");
  }

  isAuthenticated(): Observable<any> {
    return this.http.get("resource/user");
  }

  getAlbumsByTag(tag: UserTagModel): Observable<any> {
    return this.http.get<AddAlbumsToTagModel>("resource/albums/" + tag.tagId).pipe(
      map(albumsByTag => plainToClass(AddAlbumsToTagModel, albumsByTag))
    );
  }

  addAlbumsToTag(tag: UserTagModel, selectedAlbums: AddAlbumsToTagModel[]): Observable<any> {
    return this.http.post<any>("resource/albums/" + tag.tagId, selectedAlbums);
  }

  getFolderTags(folderId): Observable<FolderTagModel> {
    return this.http.get<any>("resource/tags/folder/" + folderId).pipe(
      map(folderTag => plainToClass(FolderTagModel, folderTag))
    );
  }
}
